import { hashFilters } from "@/lib/queryKeys";
import { observeQueryStart, recordQueryMetric } from "@/lib/queryObservability";
import { supabase } from "@/lib/supabase";

export type ResolveRange = "24h" | "7d" | "30d" | "all";
export type ResolveIssueQuickView = "all" | "open" | "urgent" | "escalated" | "crew" | "sub" | "customer" | "install" | "material" | "resolved";

export interface PageResponse<T> {
  data: T[];
  count: number;
  error: unknown | null;
}

export interface ResolveCallbackRow {
  id: string;
  jobId: string;
  jobLabel: string;
  callbackType: string;
  severity: string;
  source: string;
  attributedTo: string;
  ownerContractorIdAtCallback: string;
  installerContractorIdAtCallback: string;
  installerName: string;
  filedAt: string;
  filedBy: string;
  description: string;
  lifecycleBucket: "fresh" | "aging" | "stale";
  anomalyLabel?: string;
  anomalyTone?: "warning" | "destructive" | "muted";
}

export interface ResolveCallbacksSummary {
  total: number;
  last24h: number;
  last7d: number;
  last30d: number;
  fresh: number;
  aging: number;
  stale: number;
  high: number;
  ownerAttributed: number;
  unassigned: number;
}

export interface ResolveHoldbackRow {
  holdbackId: string;
  jobId: string;
  jobLabel: string;
  contractorId: string;
  contractorName: string;
  createdAmount: number;
  releasedAmount: number;
  reversedAmount: number;
  netHeld: number;
  createCount: number;
  releaseCount: number;
  reverseCount: number;
  lastEntryAt: string;
  pausedByDisputeId?: string;
  latestReason: string;
  holdbackStatus: string;
  lifecycleBucket: "active" | "paused" | "resolved";
  anomalyLabel?: string;
  anomalyTone?: "warning" | "destructive" | "muted";
}

export interface ResolveDisputeEventRow {
  id: string;
  disputeId: string;
  eventType: string;
  callbackId?: string;
  holdbackId?: string;
  jobId: string;
  jobLabel: string;
  contractorId: string;
  contractorName: string;
  actorId: string;
  actorRole: string;
  reason: string;
  createdAt: string;
  outcome?: string;
}

export interface ResolveHoldbacksSummary {
  totalHoldbacks: number;
  activeHoldbacks: number;
  pausedHoldbacks: number;
  resolvedHoldbacks: number;
  reversals: number;
  disputeOpenEvents: number;
  disputeEvents: number;
}

export interface ResolveIssueRow {
  rowId: string;
  issueId: string;
  jobId: string;
  jobLabel: string;
  customerName: string;
  address: string;
  branch: string;
  region: string;
  assignmentType: string;
  assignedCrew: string;
  assignedSubcontractor: string;
  issueType: string;
  description: string;
  status: string;
  severity: string;
  owner: string;
  source: string;
  relatedContractorId?: string;
  relatedContractorName?: string;
  resolutionNotes: string;
  photo: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  dateInProgress?: string;
  dateClosed?: string;
  daysOpen: number;
  isOpen: boolean;
  isUrgentActive: boolean;
  isEscalated: boolean;
}

export interface ResolveIssuesSummary {
  total: number;
  open: number;
  inProgress: number;
  resolved: number;
  urgent: number;
  escalated: number;
  crew: number;
  subcontractor: number;
  customer: number;
  install: number;
  material: number;
  issueTypes: string[];
  branches: string[];
  regions: string[];
}

export interface ResolveFrictionAdvisoryRow {
  id: string;
  kind: string;
  subjectKind: string;
  subjectId: string;
  rationaleKey: string;
  outcome: string;
  outcomeReason: string;
  outcomeActorId: string;
  outcomeAt?: string;
  suggestedAt?: string;
  eventAt: string;
}

export interface ResolveFrictionNegativeOverrideRow {
  id: string;
  observationId: string;
  jobId: string;
  jobLabel: string;
  outcome: string;
  reviewerId: string;
  reviewerRole: string;
  reason: string;
  recordedAt: string;
}

export interface ResolveFrictionRepeatRow {
  contractorId: string;
  contractorName: string;
  eventCount: number;
  sortAt: string;
}

export interface ResolveFrictionSummary {
  dismissed: number;
  overridden: number;
  expired: number;
  negativeOverrides: number;
  repeatDisputeContractors: number;
  repeatReversalContractors: number;
}

export interface ResolveFocusSummary {
  callbacks7d: number;
  holdbacksAttention: number;
  issuesTotal: number;
  serviceTotal: number;
  frictionTotal: number;
}

const CALLBACK_COLUMNS = [
  "id",
  "job_id",
  "job_label",
  "callback_type",
  "severity",
  "source",
  "attributed_to",
  "owner_contractor_id_at_callback",
  "installer_contractor_id_at_callback",
  "installer_name",
  "filed_at",
  "filed_by",
  "description",
  "lifecycle_bucket",
  "anomaly_label",
  "anomaly_tone",
  "sort_at",
].join(",");

const CALLBACK_SUMMARY_COLUMNS = "total,last_24h,last_7d,last_30d,fresh,aging,stale,high,owner_attributed,unassigned";
const HOLDBACK_COLUMNS = "holdback_id,job_id,job_label,contractor_id,contractor_name,created_amount,released_amount,reversed_amount,net_held,create_count,release_count,reverse_count,last_entry_at,paused_by_dispute_id,latest_reason,holdback_status,lifecycle_bucket,anomaly_label,anomaly_tone,sort_at";
const DISPUTE_COLUMNS = "id,dispute_id,event_type,callback_id,holdback_id,job_id,job_label,contractor_id,contractor_name,actor_id,actor_role,reason,created_at,outcome,sort_at";
const HOLDBACK_SUMMARY_COLUMNS = "total_holdbacks,active_holdbacks,paused_holdbacks,resolved_holdbacks,reversals,dispute_open_events,dispute_events";
const ISSUE_COLUMNS = "row_id,issue_id,job_id,job_label,customer_name,address,branch,region,assignment_type,assigned_crew,assigned_subcontractor,issue_type,description,status,severity,owner,source,related_contractor_id,related_contractor_name,resolution_notes,photo,created_at,updated_at,resolved_at,date_in_progress,date_closed,days_open,is_open,is_urgent_active,is_escalated,sort_at";
const ISSUE_SUMMARY_COLUMNS = "total,open,in_progress,resolved,urgent,escalated,crew,subcontractor,customer,install,material,issue_types,branches,regions";
const FRICTION_ADVISORY_COLUMNS = "id,kind,subject_kind,subject_id,rationale_key,outcome,outcome_reason,outcome_actor_id,outcome_at,suggested_at,event_at,sort_at";
const FRICTION_OVERRIDE_COLUMNS = "id,observation_id,job_id,job_label,outcome,reviewer_id,reviewer_role,reason,recorded_at,sort_at";
const FRICTION_REPEAT_COLUMNS = "contractor_id,contractor_name,event_count,sort_at";
const FRICTION_SUMMARY_COLUMNS = "dismissed,overridden,expired,negative_overrides,repeat_dispute_contractors,repeat_reversal_contractors";
const FOCUS_SUMMARY_COLUMNS = "callbacks_7d,holdbacks_attention,issues_total,service_total,friction_total";

function num(value: unknown): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function text(value: unknown, fallback = ""): string {
  return value == null ? fallback : String(value);
}

function arrayText(value: unknown): string[] {
  return Array.isArray(value) ? value.map((item) => String(item)).filter(Boolean) : [];
}

function emptyPage<T>(error: unknown | null = null): PageResponse<T> {
  return { data: [], count: 0, error };
}

function sanitizedSearchPattern(search: string): string {
  return `%${search.trim().replace(/[%_]/g, "").replace(/,/g, " ")}%`;
}

function rangeStart(range: ResolveRange): string | null {
  const now = Date.now();
  if (range === "24h") return new Date(now - 24 * 60 * 60 * 1000).toISOString();
  if (range === "7d") return new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString();
  if (range === "30d") return new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString();
  return null;
}

function mapBucket<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  const candidate = text(value) as T;
  return allowed.includes(candidate) ? candidate : fallback;
}

async function withPageMetrics<T>(key: string, run: () => Promise<T>): Promise<T> {
  const startedAt = observeQueryStart(key);
  try {
    const result = await run();
    recordQueryMetric({ key, startedAt, status: "success" });
    return result;
  } catch (error) {
    recordQueryMetric({ key, startedAt, status: "error", error });
    throw error;
  }
}

function mapCallback(row: Record<string, unknown>): ResolveCallbackRow {
  return {
    id: text(row.id),
    jobId: text(row.job_id),
    jobLabel: text(row.job_label, text(row.job_id)),
    callbackType: text(row.callback_type, "other"),
    severity: text(row.severity, "medium"),
    source: text(row.source, "human"),
    attributedTo: text(row.attributed_to, "unattributed"),
    ownerContractorIdAtCallback: text(row.owner_contractor_id_at_callback),
    installerContractorIdAtCallback: text(row.installer_contractor_id_at_callback),
    installerName: text(row.installer_name, "Unassigned"),
    filedAt: text(row.filed_at),
    filedBy: text(row.filed_by),
    description: text(row.description),
    lifecycleBucket: mapBucket(row.lifecycle_bucket, ["fresh", "aging", "stale"] as const, "fresh"),
    anomalyLabel: text(row.anomaly_label) || undefined,
    anomalyTone: mapBucket(row.anomaly_tone, ["warning", "destructive", "muted", ""] as const, "") || undefined,
  };
}

function mapCallbacksSummary(row: Record<string, unknown> | null | undefined): ResolveCallbacksSummary {
  return {
    total: num(row?.total),
    last24h: num(row?.last_24h),
    last7d: num(row?.last_7d),
    last30d: num(row?.last_30d),
    fresh: num(row?.fresh),
    aging: num(row?.aging),
    stale: num(row?.stale),
    high: num(row?.high),
    ownerAttributed: num(row?.owner_attributed),
    unassigned: num(row?.unassigned),
  };
}

function mapHoldback(row: Record<string, unknown>): ResolveHoldbackRow {
  return {
    holdbackId: text(row.holdback_id),
    jobId: text(row.job_id),
    jobLabel: text(row.job_label, text(row.job_id)),
    contractorId: text(row.contractor_id),
    contractorName: text(row.contractor_name, "Unknown"),
    createdAmount: num(row.created_amount),
    releasedAmount: num(row.released_amount),
    reversedAmount: num(row.reversed_amount),
    netHeld: num(row.net_held),
    createCount: num(row.create_count),
    releaseCount: num(row.release_count),
    reverseCount: num(row.reverse_count),
    lastEntryAt: text(row.last_entry_at),
    pausedByDisputeId: text(row.paused_by_dispute_id) || undefined,
    latestReason: text(row.latest_reason),
    holdbackStatus: text(row.holdback_status, "active"),
    lifecycleBucket: mapBucket(row.lifecycle_bucket, ["active", "paused", "resolved"] as const, "active"),
    anomalyLabel: text(row.anomaly_label) || undefined,
    anomalyTone: mapBucket(row.anomaly_tone, ["warning", "destructive", "muted", ""] as const, "") || undefined,
  };
}

function mapDispute(row: Record<string, unknown>): ResolveDisputeEventRow {
  return {
    id: text(row.id),
    disputeId: text(row.dispute_id),
    eventType: text(row.event_type),
    callbackId: text(row.callback_id) || undefined,
    holdbackId: text(row.holdback_id) || undefined,
    jobId: text(row.job_id),
    jobLabel: text(row.job_label, text(row.job_id)),
    contractorId: text(row.contractor_id),
    contractorName: text(row.contractor_name, "Unknown"),
    actorId: text(row.actor_id),
    actorRole: text(row.actor_role),
    reason: text(row.reason),
    createdAt: text(row.created_at),
    outcome: text(row.outcome) || undefined,
  };
}

function mapHoldbacksSummary(row: Record<string, unknown> | null | undefined): ResolveHoldbacksSummary {
  return {
    totalHoldbacks: num(row?.total_holdbacks),
    activeHoldbacks: num(row?.active_holdbacks),
    pausedHoldbacks: num(row?.paused_holdbacks),
    resolvedHoldbacks: num(row?.resolved_holdbacks),
    reversals: num(row?.reversals),
    disputeOpenEvents: num(row?.dispute_open_events),
    disputeEvents: num(row?.dispute_events),
  };
}

function mapIssue(row: Record<string, unknown>): ResolveIssueRow {
  return {
    rowId: text(row.row_id),
    issueId: text(row.issue_id),
    jobId: text(row.job_id),
    jobLabel: text(row.job_label, text(row.job_id)),
    customerName: text(row.customer_name, "Unknown"),
    address: text(row.address),
    branch: text(row.branch),
    region: text(row.region),
    assignmentType: text(row.assignment_type),
    assignedCrew: text(row.assigned_crew),
    assignedSubcontractor: text(row.assigned_subcontractor),
    issueType: text(row.issue_type, "Other"),
    description: text(row.description),
    status: text(row.status, "Open"),
    severity: text(row.severity, "Medium"),
    owner: text(row.owner),
    source: text(row.source),
    relatedContractorId: text(row.related_contractor_id) || undefined,
    relatedContractorName: text(row.related_contractor_name) || undefined,
    resolutionNotes: text(row.resolution_notes),
    photo: text(row.photo),
    createdAt: text(row.created_at),
    updatedAt: text(row.updated_at),
    resolvedAt: text(row.resolved_at) || undefined,
    dateInProgress: text(row.date_in_progress) || undefined,
    dateClosed: text(row.date_closed) || undefined,
    daysOpen: num(row.days_open),
    isOpen: Boolean(row.is_open),
    isUrgentActive: Boolean(row.is_urgent_active),
    isEscalated: Boolean(row.is_escalated),
  };
}

function mapIssuesSummary(row: Record<string, unknown> | null | undefined): ResolveIssuesSummary {
  return {
    total: num(row?.total),
    open: num(row?.open),
    inProgress: num(row?.in_progress),
    resolved: num(row?.resolved),
    urgent: num(row?.urgent),
    escalated: num(row?.escalated),
    crew: num(row?.crew),
    subcontractor: num(row?.subcontractor),
    customer: num(row?.customer),
    install: num(row?.install),
    material: num(row?.material),
    issueTypes: arrayText(row?.issue_types),
    branches: arrayText(row?.branches),
    regions: arrayText(row?.regions),
  };
}

function mapFrictionAdvisory(row: Record<string, unknown>): ResolveFrictionAdvisoryRow {
  return {
    id: text(row.id),
    kind: text(row.kind),
    subjectKind: text(row.subject_kind),
    subjectId: text(row.subject_id),
    rationaleKey: text(row.rationale_key),
    outcome: text(row.outcome),
    outcomeReason: text(row.outcome_reason),
    outcomeActorId: text(row.outcome_actor_id),
    outcomeAt: text(row.outcome_at) || undefined,
    suggestedAt: text(row.suggested_at) || undefined,
    eventAt: text(row.event_at),
  };
}

function mapFrictionOverride(row: Record<string, unknown>): ResolveFrictionNegativeOverrideRow {
  return {
    id: text(row.id),
    observationId: text(row.observation_id),
    jobId: text(row.job_id),
    jobLabel: text(row.job_label, text(row.job_id)),
    outcome: text(row.outcome),
    reviewerId: text(row.reviewer_id),
    reviewerRole: text(row.reviewer_role),
    reason: text(row.reason),
    recordedAt: text(row.recorded_at),
  };
}

function mapRepeat(row: Record<string, unknown>): ResolveFrictionRepeatRow {
  return {
    contractorId: text(row.contractor_id),
    contractorName: text(row.contractor_name, "Unknown"),
    eventCount: num(row.event_count),
    sortAt: text(row.sort_at),
  };
}

function mapFrictionSummary(row: Record<string, unknown> | null | undefined): ResolveFrictionSummary {
  return {
    dismissed: num(row?.dismissed),
    overridden: num(row?.overridden),
    expired: num(row?.expired),
    negativeOverrides: num(row?.negative_overrides),
    repeatDisputeContractors: num(row?.repeat_dispute_contractors),
    repeatReversalContractors: num(row?.repeat_reversal_contractors),
  };
}

function mapFocusSummary(row: Record<string, unknown> | null | undefined): ResolveFocusSummary {
  return {
    callbacks7d: num(row?.callbacks_7d),
    holdbacksAttention: num(row?.holdbacks_attention),
    issuesTotal: num(row?.issues_total),
    serviceTotal: num(row?.service_total),
    frictionTotal: num(row?.friction_total),
  };
}

function applyRange<T>(builder: T, column: string, range: ResolveRange): T {
  const start = rangeStart(range);
  if (!start) return builder;
  return (builder as any).gte(column, start) as T;
}

export const ResolvePageService = {
  async getCallbacksPage(query: { page: number; pageSize: number; range: ResolveRange; search?: string; refreshToken?: number }): Promise<PageResponse<ResolveCallbackRow>> {
    const key = `resolve:callbacks:${hashFilters(query)}`;
    return withPageMetrics(key, async () => {
      const from = Math.max(0, query.page) * Math.max(1, query.pageSize);
      const to = from + Math.max(1, query.pageSize) - 1;
      let builder = supabase.from("v_resolve_callbacks_ui").select(CALLBACK_COLUMNS, { count: "exact" });
      builder = applyRange(builder, "filed_at", query.range);
      const search = query.search?.trim();
      if (search) {
        const pattern = sanitizedSearchPattern(search);
        builder = builder.or([
          `job_label.ilike.${pattern}`,
          `installer_name.ilike.${pattern}`,
          `callback_type.ilike.${pattern}`,
          `description.ilike.${pattern}`,
        ].join(","));
      }
      const { data, error, count } = await builder.order("sort_at", { ascending: false }).range(from, to);
      return error || !data ? emptyPage(error) : { data: (data as Record<string, unknown>[]).map(mapCallback), count: count ?? 0, error };
    });
  },

  async getCallbacksSummary(refreshToken?: number): Promise<{ data: ResolveCallbacksSummary; error: unknown | null }> {
    return withPageMetrics(`resolve:callbacks-summary:${refreshToken ?? 0}`, async () => {
      const { data, error } = await supabase.from("v_resolve_callbacks_summary").select(CALLBACK_SUMMARY_COLUMNS).maybeSingle();
      return { data: mapCallbacksSummary(data as Record<string, unknown> | null), error };
    });
  },

  async getHoldbacksPage(query: { page: number; pageSize: number; bucket?: "all" | "active" | "paused" | "resolved"; search?: string; refreshToken?: number }): Promise<PageResponse<ResolveHoldbackRow>> {
    const key = `resolve:holdbacks:${hashFilters(query)}`;
    return withPageMetrics(key, async () => {
      const from = Math.max(0, query.page) * Math.max(1, query.pageSize);
      const to = from + Math.max(1, query.pageSize) - 1;
      let builder = supabase.from("v_resolve_holdbacks_ui").select(HOLDBACK_COLUMNS, { count: "exact" });
      if (query.bucket && query.bucket !== "all") builder = builder.eq("lifecycle_bucket", query.bucket);
      const search = query.search?.trim();
      if (search) {
        const pattern = sanitizedSearchPattern(search);
        builder = builder.or([
          `holdback_id.ilike.${pattern}`,
          `job_label.ilike.${pattern}`,
          `contractor_name.ilike.${pattern}`,
          `latest_reason.ilike.${pattern}`,
        ].join(","));
      }
      const { data, error, count } = await builder.order("sort_at", { ascending: false }).range(from, to);
      return error || !data ? emptyPage(error) : { data: (data as Record<string, unknown>[]).map(mapHoldback), count: count ?? 0, error };
    });
  },

  async getDisputeEventsPage(query: { page: number; pageSize: number; search?: string; refreshToken?: number }): Promise<PageResponse<ResolveDisputeEventRow>> {
    const key = `resolve:disputes:${hashFilters(query)}`;
    return withPageMetrics(key, async () => {
      const from = Math.max(0, query.page) * Math.max(1, query.pageSize);
      const to = from + Math.max(1, query.pageSize) - 1;
      let builder = supabase.from("v_resolve_dispute_events_ui").select(DISPUTE_COLUMNS, { count: "exact" });
      const search = query.search?.trim();
      if (search) {
        const pattern = sanitizedSearchPattern(search);
        builder = builder.or([
          `dispute_id.ilike.${pattern}`,
          `job_label.ilike.${pattern}`,
          `contractor_name.ilike.${pattern}`,
          `reason.ilike.${pattern}`,
        ].join(","));
      }
      const { data, error, count } = await builder.order("sort_at", { ascending: false }).range(from, to);
      return error || !data ? emptyPage(error) : { data: (data as Record<string, unknown>[]).map(mapDispute), count: count ?? 0, error };
    });
  },

  async getHoldbacksSummary(refreshToken?: number): Promise<{ data: ResolveHoldbacksSummary; error: unknown | null }> {
    return withPageMetrics(`resolve:holdbacks-summary:${refreshToken ?? 0}`, async () => {
      const { data, error } = await supabase.from("v_resolve_holdbacks_summary").select(HOLDBACK_SUMMARY_COLUMNS).maybeSingle();
      return { data: mapHoldbacksSummary(data as Record<string, unknown> | null), error };
    });
  },

  async getIssuesPage(query: {
    page: number;
    pageSize: number;
    quickView?: ResolveIssueQuickView;
    search?: string;
    issueType?: string;
    severity?: string;
    branch?: string;
    region?: string;
    refreshToken?: number;
  }): Promise<PageResponse<ResolveIssueRow>> {
    const key = `resolve:issues:${hashFilters(query)}`;
    return withPageMetrics(key, async () => {
      const from = Math.max(0, query.page) * Math.max(1, query.pageSize);
      const to = from + Math.max(1, query.pageSize) - 1;
      let builder = supabase.from("v_resolve_issues_ui").select(ISSUE_COLUMNS, { count: "exact" });

      switch (query.quickView ?? "open") {
        case "open": builder = builder.eq("is_open", true); break;
        case "urgent": builder = builder.eq("is_urgent_active", true); break;
        case "escalated": builder = builder.eq("is_escalated", true); break;
        case "crew": builder = builder.eq("assignment_type", "Internal Crew").eq("is_open", true); break;
        case "sub": builder = builder.eq("assignment_type", "Subcontractor").eq("is_open", true); break;
        case "customer": builder = builder.eq("issue_type", "Customer Complaint").eq("is_open", true); break;
        case "install": builder = builder.eq("issue_type", "Install Issue").eq("is_open", true); break;
        case "material": builder = builder.in("issue_type", ["Material Issue", "Ordering Issue"]).eq("is_open", true); break;
        case "resolved": builder = builder.in("status", ["Resolved", "Closed"]); break;
        case "all": default: break;
      }

      if (query.issueType && query.issueType !== "All") builder = builder.eq("issue_type", query.issueType);
      if (query.severity && query.severity !== "All") builder = builder.eq("severity", query.severity);
      if (query.branch && query.branch !== "all") builder = builder.eq("branch", query.branch);
      if (query.region && query.region !== "all") builder = builder.eq("region", query.region);

      const search = query.search?.trim();
      if (search) {
        const pattern = sanitizedSearchPattern(search);
        builder = builder.or([
          `issue_id.ilike.${pattern}`,
          `job_label.ilike.${pattern}`,
          `customer_name.ilike.${pattern}`,
          `address.ilike.${pattern}`,
          `description.ilike.${pattern}`,
          `owner.ilike.${pattern}`,
        ].join(","));
      }

      const { data, error, count } = await builder
        .order("is_open", { ascending: false })
        .order("is_escalated", { ascending: false })
        .order("sort_at", { ascending: false })
        .range(from, to);
      return error || !data ? emptyPage(error) : { data: (data as Record<string, unknown>[]).map(mapIssue), count: count ?? 0, error };
    });
  },

  async getIssuesSummary(refreshToken?: number): Promise<{ data: ResolveIssuesSummary; error: unknown | null }> {
    return withPageMetrics(`resolve:issues-summary:${refreshToken ?? 0}`, async () => {
      const { data, error } = await supabase.from("v_resolve_issues_summary").select(ISSUE_SUMMARY_COLUMNS).maybeSingle();
      return { data: mapIssuesSummary(data as Record<string, unknown> | null), error };
    });
  },

  async getFrictionAdvisoriesPage(query: { page: number; pageSize: number; range: ResolveRange; outcome?: string; refreshToken?: number }): Promise<PageResponse<ResolveFrictionAdvisoryRow>> {
    const key = `resolve:friction-advisories:${hashFilters(query)}`;
    return withPageMetrics(key, async () => {
      const from = Math.max(0, query.page) * Math.max(1, query.pageSize);
      const to = from + Math.max(1, query.pageSize) - 1;
      let builder = supabase.from("v_resolve_friction_advisories_ui").select(FRICTION_ADVISORY_COLUMNS, { count: "exact" });
      builder = applyRange(builder, "event_at", query.range);
      if (query.outcome && query.outcome !== "all") builder = builder.eq("outcome", query.outcome);
      const { data, error, count } = await builder.order("sort_at", { ascending: false }).range(from, to);
      return error || !data ? emptyPage(error) : { data: (data as Record<string, unknown>[]).map(mapFrictionAdvisory), count: count ?? 0, error };
    });
  },

  async getFrictionNegativeOverridesPage(query: { page: number; pageSize: number; range: ResolveRange; refreshToken?: number }): Promise<PageResponse<ResolveFrictionNegativeOverrideRow>> {
    const key = `resolve:friction-overrides:${hashFilters(query)}`;
    return withPageMetrics(key, async () => {
      const from = Math.max(0, query.page) * Math.max(1, query.pageSize);
      const to = from + Math.max(1, query.pageSize) - 1;
      let builder = supabase.from("v_resolve_friction_negative_overrides_ui").select(FRICTION_OVERRIDE_COLUMNS, { count: "exact" });
      builder = applyRange(builder, "recorded_at", query.range);
      const { data, error, count } = await builder.order("sort_at", { ascending: false }).range(from, to);
      return error || !data ? emptyPage(error) : { data: (data as Record<string, unknown>[]).map(mapFrictionOverride), count: count ?? 0, error };
    });
  },

  async getFrictionRepeatDisputesPage(query: { page: number; pageSize: number; refreshToken?: number }): Promise<PageResponse<ResolveFrictionRepeatRow>> {
    return this.getRepeatPage("v_resolve_friction_repeat_disputes_ui", `resolve:friction-repeat-disputes:${hashFilters(query)}`, query);
  },

  async getFrictionRepeatReversalsPage(query: { page: number; pageSize: number; refreshToken?: number }): Promise<PageResponse<ResolveFrictionRepeatRow>> {
    return this.getRepeatPage("v_resolve_friction_repeat_reversals_ui", `resolve:friction-repeat-reversals:${hashFilters(query)}`, query);
  },

  async getRepeatPage(table: string, key: string, query: { page: number; pageSize: number }): Promise<PageResponse<ResolveFrictionRepeatRow>> {
    return withPageMetrics(key, async () => {
      const from = Math.max(0, query.page) * Math.max(1, query.pageSize);
      const to = from + Math.max(1, query.pageSize) - 1;
      const { data, error, count } = await supabase
        .from(table)
        .select(FRICTION_REPEAT_COLUMNS, { count: "exact" })
        .order("event_count", { ascending: false })
        .order("sort_at", { ascending: false })
        .range(from, to);
      return error || !data ? emptyPage(error) : { data: (data as Record<string, unknown>[]).map(mapRepeat), count: count ?? 0, error };
    });
  },

  async getFrictionSummary(refreshToken?: number): Promise<{ data: ResolveFrictionSummary; error: unknown | null }> {
    return withPageMetrics(`resolve:friction-summary:${refreshToken ?? 0}`, async () => {
      const { data, error } = await supabase.from("v_resolve_friction_summary").select(FRICTION_SUMMARY_COLUMNS).maybeSingle();
      return { data: mapFrictionSummary(data as Record<string, unknown> | null), error };
    });
  },

  async getFocusSummary(refreshToken?: number): Promise<{ data: ResolveFocusSummary; error: unknown | null }> {
    return withPageMetrics(`resolve:focus-summary:${refreshToken ?? 0}`, async () => {
      const { data, error } = await supabase.from("v_resolve_focus_summary").select(FOCUS_SUMMARY_COLUMNS).maybeSingle();
      return { data: mapFocusSummary(data as Record<string, unknown> | null), error };
    });
  },
};
